import * as React from "react"

import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"
import { CymaticField } from "../CymaticField/CymaticField"

const loaderVariants = cva(
  "inline-flex items-center justify-center shrink-0 text-coval-text",
  {
    variants: {
      size: {
        sm: "size-4",
        md: "size-6",
        lg: "size-10",
        xl: "size-16",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
)

/**
 * The pixel box each variant renders at, used until the host element has been
 * measured. The particle field needs a pixel size, but callers size loaders
 * with utility classes, so the measured box is what actually governs.
 */
const VARIANT_PIXEL_SIZE = {
  sm: 16,
  md: 24,
  lg: 40,
  xl: 64,
} as const

export interface LoaderProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, "aria-label">,
    VariantProps<typeof loaderVariants> {
  label?: string
}

const Loader = React.forwardRef<HTMLSpanElement, LoaderProps>(
  ({ className, size, label = "Loading", ...props }, ref) => {
    const hostRef = React.useRef<HTMLSpanElement | null>(null)
    const [pixelSize, setPixelSize] = React.useState<number>(
      VARIANT_PIXEL_SIZE[size ?? "md"],
    )

    const setHost = React.useCallback(
      (node: HTMLSpanElement | null) => {
        hostRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref],
    )

    // A caller can override the variant's box with a utility class, so read the
    // rendered size rather than trusting the variant.
    React.useEffect(() => {
      const host = hostRef.current
      if (!host || typeof ResizeObserver === "undefined") {
        return
      }

      const measure = () => {
        const measured = Math.round(
          Math.min(host.clientWidth, host.clientHeight),
        )
        if (measured > 0) {
          setPixelSize(measured)
        }
      }
      measure()

      const observer = new ResizeObserver(measure)
      observer.observe(host)
      return () => observer.disconnect()
    }, [])

    return (
      <span
        ref={setHost}
        role="status"
        aria-label={label}
        className={cn(loaderVariants({ size }), className)}
        {...props}
      >
        <CymaticField aria-hidden="true" animated size={pixelSize} />
        <span className="sr-only">{label}</span>
      </span>
    )
  },
)

Loader.displayName = "Loader"

export { Loader, loaderVariants }
